import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth, getUserRole } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';

interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
  fullName: string;
}

const Login: React.FC = () => {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if register=true is in the URL query params
    const params = new URLSearchParams(location.search);
    setIsRegister(params.get('register') === 'true');
  }, [location]);

  // Watch for user changes and redirect when user data is available
  useEffect(() => {
    if (user) {
      navigate(getDashboardLink(), { replace: true });
    }
  }, [user, navigate]);

  const getDashboardLink = () => {
    if (!user) return '/dashboard';
    
    switch (getUserRole(user)) {
      case 'admin':
        return '/admin';
      case 'speaker':
        return '/speaker';
      default:
        return '/dashboard';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (isRegister) {
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match');
        }
        
        const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            password,
            full_name: fullName,
          }),
        });

        if (!response.ok) {
          const data = await response.json();
          // Handle different error response formats from FastAPI
          if (data.detail && typeof data.detail === 'object' && Array.isArray(data.detail)) {
            // Handle FastAPI validation errors which come as an array
            const errorMessages: string[] = data.detail.map((error: any) => {
              if (error.loc && error.loc.includes('password') && error.type === 'value_error.any_str.min_length') {
                return `Password must be at least 8 characters long`;
              }
              return `${error.loc ? error.loc.join('.').replace(/^body\./, '') : 'Error'}: ${error.msg}`;
            });
            throw new Error(errorMessages.join(', '));
          } else if (data.detail && typeof data.detail === 'object') {
            // Handle other object-type errors
            const errorMessages: string[] = [];
            for (const field in data.detail) {
              if (Array.isArray(data.detail[field])) {
                data.detail[field].forEach((error: { msg: string }) => {
                  if (field === 'password' && error.msg.includes('min_length')) {
                    errorMessages.push('Password must be at least 8 characters long');
                  } else {
                    errorMessages.push(`${field}: ${error.msg}`);
                  }
                });
              } else if (typeof data.detail[field] === 'string') {
                errorMessages.push(`${field}: ${data.detail[field]}`);
              }
            }
            throw new Error(errorMessages.join(', ') || 'Validation error');
          } else {
            throw new Error(data.detail || 'Registration failed');
          }
        }

        // After successful registration, log the user in
        await login(email, password);
      } else {
        await login(email, password);
      }
      // Navigation will happen automatically when user data is set
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#00529B] to-[#003D74] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 text-center">
            {isRegister ? 'Register for the Conference website' : 'Sign in to your account'}
          </h2>
          <p className="mt-4 text-center text-sm text-gray-600">
            {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
            <Link
              to={isRegister ? '/login' : '/login?register=true'}
              className="font-medium text-[#00529B] hover:text-[#003D74]"
            >
              {isRegister ? 'Sign in' : 'Register now'}
            </Link>
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{error}</h3>
                </div>
              </div>
            </div>
          )}
          {isRegister && (
            <div className="rounded-md bg-orange-50 border border-orange-200 p-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-orange-500 mr-3 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="text-sm text-orange-800">
                    <p>
                      <strong>Important:</strong> Creating an account on this website is not the same as registering as an attendee.
                    </p>
                    <p className="mt-1">
                      To attend Language Intelligence 2025, you must register via Eventbrite.
                    </p>
                  </div>
                </div>
                <a
                  href="https://www.eventbrite.co.uk/e/language-intelligence-2025-tickets-1299678576609"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md transition-colors shadow-sm whitespace-nowrap"
                >
                  Register on Eventbrite
                  <svg className="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </a>
              </div>
            </div>
          )}
          <div className="space-y-4">
            {isRegister && (
              <div>
                <label htmlFor="full-name" className="block text-sm font-medium text-gray-700">
                  Full name
                </label>
                <input
                  id="full-name"
                  name="fullName"
                  type="text"
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#00529B] focus:border-[#00529B] sm:text-sm"
                  placeholder="Full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </div>
            )}
            <div>
              <label htmlFor="email-address" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#00529B] focus:border-[#00529B] sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete={isRegister ? 'new-password' : 'current-password'}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#00529B] focus:border-[#00529B] sm:text-sm"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {isRegister && (
              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                  Confirm password
                </label>
                <input
                  id="confirm-password"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#00529B] focus:border-[#00529B] sm:text-sm"
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#00529B] hover:bg-[#003D74] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00529B] ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? (
                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </span>
              ) : null}
              {isLoading
                ? isRegister
                  ? 'Registering...'
                  : 'Signing in...'
                : isRegister
                ? 'Register'
                : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
